import React, { useEffect, useState } from "react";
import { api, formatApiError, API } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import DashboardLayout from "../components/DashboardLayout";
import { BarChart3, ShieldCheck, Users, Check, X, Eye, Save, MessageCircle, Plus, Trash2, Briefcase, Download, Pencil, Trophy, UserPlus, GraduationCap, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { t } = useI18n();
  const { user: authUser } = useAuth();
  const isSuperAdmin = !!authUser?.is_super_admin || (authUser?.email || "").toLowerCase() === "email@seayisyen.net";
  const [section, setSection] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ role: "", membership: "" });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [waAccounts, setWaAccounts] = useState([]);
  const [waForm, setWaForm] = useState({ label: "", phone: "", region: "", note: "" });
  const [waEditId, setWaEditId] = useState(null);
  const [svcOrders, setSvcOrders] = useState([]);
  const [userEditModal, setUserEditModal] = useState(null); // user object
  const [userForm, setUserForm] = useState({ name: "", email: "", role: "", membership: "", country: "", phone: "" });

  useEffect(() => {
    if (!window.location.hash) window.location.hash = "#analytics";
    const onHash = () => setSection((window.location.hash || "#analytics").replace("#", ""));
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const reload = async () => {
    try {
      const [a, e, u, w, s] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/admin/enrollments"),
        api.get("/admin/users"),
        api.get("/admin/whatsapp"),
        api.get("/admin/services"),
      ]);
      setAnalytics(a.data);
      setEnrollments(e.data || []);
      setUsers(u.data || []);
      setWaAccounts(w.data || []);
      setSvcOrders(s.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { reload(); }, []);

  const navItems = [
    { id: "analytics", hash: "#analytics", icon: BarChart3, label: t("admin.analytics") },
    { id: "payments", hash: "#payments", icon: ShieldCheck, label: t("admin.payments") },
    { id: "services", hash: "#services", icon: Briefcase, label: "Services" },
    { id: "users", hash: "#users", icon: GraduationCap, label: t("admin.studentMgmt", "🎓 Student Management") },
    { id: "competitors", hash: "#competitors", icon: Trophy, label: t("admin.competitionMgmt", "🏆 Competition Management") },
    { id: "whatsapp", hash: "#whatsapp", icon: MessageCircle, label: "WhatsApp" },
    ...(isSuperAdmin ? [{ id: "manual-reg", hash: "#manual-reg", icon: UserPlus, label: t("admin.manualRegister", "Manual Registration") }] : []),
  ];

  const approveService = async (id) => {
    try { await api.post(`/admin/services/${id}/approve`); toast.success("Approved"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const rejectService = async (id) => {
    try { await api.post(`/admin/services/${id}/reject`); toast.success("Rejected"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const updateProjectStatus = async (orderId, status) => {
    try { await api.post(`/admin/services/${orderId}/status`, { project_status: status }); toast.success("Status updated"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const saveWhatsapp = async (e) => {
    e?.preventDefault?.();
    if (!waForm.label || !waForm.phone) return;
    try {
      if (waEditId) {
        await api.patch(`/admin/whatsapp/${waEditId}`, { ...waForm, is_active: true });
        toast.success("Updated");
      } else {
        await api.post("/admin/whatsapp", { ...waForm, is_active: true });
        toast.success("Added");
      }
      setWaForm({ label: "", phone: "", region: "", note: "" });
      setWaEditId(null);
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const toggleWhatsapp = async (w) => {
    try {
      await api.patch(`/admin/whatsapp/${w.id}`, { ...w, is_active: !w.is_active });
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const deleteWhatsapp = async (id) => {
    if (!window.confirm("Delete this account?")) return;
    try { await api.delete(`/admin/whatsapp/${id}`); toast.success("Deleted"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const approve = async (id) => {
    try { await api.post(`/admin/enrollments/${id}/approve`); toast.success("Approved"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/enrollments/${id}/reject`); toast.success("Rejected"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const saveUser = async (uid) => {
    try {
      await api.patch(`/admin/users/${uid}`, editForm);
      toast.success("Updated");
      setEditingUser(null);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  // ---- User modal handlers (Modifier / Supprimer in users tab) ----
  const openEditUser = (u) => {
    setUserForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role || "student",
      membership: u.membership || "non_member",
      country: u.country || "",
      phone: u.phone || "",
    });
    setUserEditModal(u);
  };

  const saveUserModal = async () => {
    if (!userEditModal) return;
    try {
      await api.patch(`/admin/users/${userEditModal.id}`, userForm);
      toast.success("Utilisateur mis à jour");
      setUserEditModal(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteUser = async (u) => {
    if (!u) return;
    if (authUser && (u.id === authUser.id || (u.email || "").toLowerCase() === (authUser.email || "").toLowerCase())) {
      toast.error("Vous ne pouvez pas supprimer votre propre compte.");
      return;
    }
    if (!window.confirm(`Supprimer définitivement ${u.email} ?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utilisateur supprimé");
      setUserEditModal(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadUrl = (p) => `${API}/files/download?path=${encodeURIComponent(p)}`;

  const pending = enrollments.filter((e) => e.payment_status === "pending_review");

  return (
    <DashboardLayout title="Admin Console" navItems={navItems}>
      {section === "analytics" && analytics && (
        <section data-testid="admin-analytics" className="space-y-6">
          {/* Operations snapshot — Course pipeline vs Competition pipeline */}
          <div className="grid md:grid-cols-2 gap-4" data-testid="ops-snapshot">
            {/* Course pipeline */}
            <div className="card-imperial p-6 relative overflow-hidden" data-testid="ops-course-card">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline text-[#d4af37] flex items-center gap-2">
                    <GraduationCap size={12} /> Course pipeline
                  </div>
                  <div className="font-serif text-2xl mt-1">{(analytics.course_pipeline?.total_paid || 0)} <span className="text-sm text-neutral-500 font-condensed">paid seats</span></div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-3xl text-[#d4af37]">${(analytics.revenue || 0).toLocaleString()}</div>
                  <div className="overline text-neutral-500">revenue</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-[#1a1a1a]">
                <div data-testid="ops-monday-paid">
                  <div className="overline text-neutral-500">Monday</div>
                  <div className="font-serif text-2xl mt-1">{analytics.course_pipeline?.monday_paid || 0}</div>
                  <div className="text-[10px] text-neutral-500 font-condensed tracking-widest uppercase">Paid · $499/$399</div>
                </div>
                <div data-testid="ops-thursday-paid">
                  <div className="overline text-[#e6192b]">Thursday AI</div>
                  <div className="font-serif text-2xl mt-1 text-[#e6192b]">{analytics.course_pipeline?.thursday_paid || 0}</div>
                  <div className="text-[10px] text-neutral-500 font-condensed tracking-widest uppercase">Paid · $750</div>
                </div>
                <div data-testid="ops-pending-review" className={analytics.course_pipeline?.pending_review > 0 ? "" : ""}>
                  <div className="overline text-neutral-500">Pending review</div>
                  <div className={`font-serif text-2xl mt-1 ${analytics.course_pipeline?.pending_review > 0 ? "text-amber-300" : "text-neutral-500"}`}>{analytics.course_pipeline?.pending_review || 0}</div>
                  <div className="text-[10px] text-neutral-500 font-condensed tracking-widest uppercase">Awaiting approval</div>
                </div>
              </div>
              <a href="#payments" className="absolute bottom-4 right-5 text-xs text-[#d4af37] hover:text-white font-condensed tracking-widest uppercase" data-testid="ops-course-cta">
                Approve queue →
              </a>
            </div>

            {/* Competition pipeline */}
            <div className="card-imperial p-6 relative overflow-hidden" data-testid="ops-competition-card">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#e6192b] to-transparent" />
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline text-[#e6192b] flex items-center gap-2">
                    <Trophy size={12} /> Competition pipeline
                  </div>
                  <div className="font-serif text-2xl mt-1">{analytics.competition_pipeline?.total || 0} <span className="text-sm text-neutral-500 font-condensed">competitors</span></div>
                </div>
                <div className="text-right">
                  <div className={`inline-block px-2 py-0.5 text-[10px] font-condensed tracking-widest uppercase border ${
                    analytics.competition_pipeline?.phase === "registration" ? "border-[#d4af37]/40 text-[#d4af37]" :
                    analytics.competition_pipeline?.phase === "voting" ? "border-emerald-700/50 text-emerald-300" :
                    analytics.competition_pipeline?.phase === "final" ? "border-blue-700/50 text-blue-300" :
                    "border-neutral-700 text-neutral-400"
                  }`} data-testid="ops-comp-phase">
                    {analytics.competition_pipeline?.phase || "—"}
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-1">closes Jun 11</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-[#1a1a1a] mb-2">
                <div data-testid="ops-comp-total">
                  <div className="overline text-neutral-500">Registered</div>
                  <div className="font-serif text-2xl mt-1">{analytics.competition_pipeline?.total || 0}</div>
                </div>
                <div data-testid="ops-comp-with-site">
                  <div className="overline text-neutral-500">With website</div>
                  <div className="font-serif text-2xl mt-1 text-[#d4af37]">{analytics.competition_pipeline?.with_website || 0}</div>
                </div>
              </div>
              {analytics.competition_pipeline?.top3?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#1a1a1a]" data-testid="ops-comp-top3">
                  <div className="overline text-neutral-500 mb-2">Top 3 by votes</div>
                  <div className="space-y-1.5">
                    {analytics.competition_pipeline.top3.map((c, i) => (
                      <div key={c.id || i} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <span className="overline text-[#d4af37] w-6">#{i + 1}</span>
                          <span>{c.name}</span>
                        </span>
                        <span className="font-condensed text-[#d4af37]">{c.votes || 0} votes</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <a href="#competitors" className="absolute bottom-4 right-5 text-xs text-[#e6192b] hover:text-white font-condensed tracking-widest uppercase" data-testid="ops-comp-cta">
                Manage →
              </a>
            </div>
          </div>

          {/* Existing stat row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label={t("admin.revenue")} value={`$${(analytics.revenue || 0).toLocaleString()}`} accent />
            <Stat label={t("admin.totalUsers")} value={analytics.total_users} />
            <Stat label={t("admin.paidEnrollments")} value={analytics.paid_enrollments} />
            <Stat label={t("admin.pendingEnrollments")} value={analytics.pending_enrollments} highlight={analytics.pending_enrollments > 0} />
          </div>
          <div className="card-imperial p-6">
            <div className="overline mb-3">By Tier (paid)</div>
            <div className="flex gap-6 flex-wrap">
              {Object.entries(analytics.by_tier || {}).map(([k, v]) => (
                <div key={k} className="flex items-baseline gap-2">
                  <div className="font-serif text-4xl text-[#d4af37]">{v}</div>
                  <div className="overline">{k}</div>
                </div>
              ))}
              {Object.keys(analytics.by_tier || {}).length === 0 && <div className="text-neutral-500 text-sm">No paid enrollments yet.</div>}
            </div>
          </div>
        </section>
      )}

      {section === "payments" && (
        <section data-testid="admin-payments">
          <h2 className="font-serif text-3xl mb-6">{t("admin.payments")}</h2>
          {pending.length === 0 ? (
            <div className="text-neutral-500 text-sm">{t("admin.noPending")}</div>
          ) : (
            <div className="space-y-3">
              {pending.map((e) => (
                <div key={e.id} className="card-imperial p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`pay-row-${e.id}`}>
                  <div className="flex-1">
                    <div className="font-serif text-lg">{e.user?.name} <span className="text-neutral-500 text-sm">— {e.user?.email}</span></div>
                    <div className="overline mt-1">{e.payment_method} · {e.region} · ${e.amount}</div>
                  </div>
                  <button
                    onClick={() => setPreviewUrl(downloadUrl(e.screenshot_path))}
                    className="btn-outline-gold text-xs"
                    data-testid={`pay-view-${e.id}`}
                  >
                    <Eye size={12} /> {t("admin.viewProof")}
                  </button>
                  <button onClick={() => approve(e.id)} className="btn-imperial text-xs" data-testid={`pay-approve-${e.id}`}>
                    <Check size={12} /> {t("admin.approve")}
                  </button>
                  <button onClick={() => reject(e.id)} className="btn-outline-gold text-xs border-[#e6192b] text-[#e6192b]" data-testid={`pay-reject-${e.id}`}>
                    <X size={12} /> {t("admin.reject")}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10">
            <div className="overline mb-3">All enrollments</div>
            <div className="card-imperial overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#0a0a0a] border-b border-[#1a1a1a]">
                  <tr>
                    <th className="text-left px-5 py-3 overline">User</th>
                    <th className="text-left px-5 py-3 overline">Tier</th>
                    <th className="text-left px-5 py-3 overline">Method</th>
                    <th className="text-left px-5 py-3 overline">Amount</th>
                    <th className="text-left px-5 py-3 overline">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map((e) => (
                    <tr key={e.id} className="border-b border-[#141414] last:border-0">
                      <td className="px-5 py-3">{e.user?.email || e.user_id}</td>
                      <td className="px-5 py-3">{e.tier}</td>
                      <td className="px-5 py-3">{e.payment_method}</td>
                      <td className="px-5 py-3">${e.amount}</td>
                      <td className="px-5 py-3">
                        <span className={
                          e.payment_status === "paid" ? "text-[#d4af37]"
                          : e.payment_status === "pending_review" ? "text-yellow-400"
                          : "text-neutral-500"
                        }>{e.payment_status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {previewUrl && (
            <div
              className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
              onClick={() => setPreviewUrl(null)}
              data-testid="proof-modal"
            >
              <div className="bg-[#0a0a0a] border border-[#222] max-w-3xl max-h-[85vh] overflow-auto p-4">
                <img src={previewUrl} alt="proof" className="max-w-full" />
                <div className="text-center mt-3">
                  <button onClick={() => setPreviewUrl(null)} className="btn-outline-gold text-xs">Close</button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {section === "users" && (
        <section data-testid="admin-users">
          <h2 className="font-serif text-3xl mb-6">{t("admin.users")}</h2>
          <div className="card-imperial overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0a0a0a] border-b border-[#1a1a1a]">
                <tr>
                  <th className="text-left px-5 py-3 overline">Name</th>
                  <th className="text-left px-5 py-3 overline">Email</th>
                  <th className="text-left px-5 py-3 overline">Country</th>
                  <th className="text-left px-5 py-3 overline">Membership</th>
                  <th className="text-left px-5 py-3 overline">Role</th>
                  <th className="text-right px-5 py-3 overline">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-[#141414] last:border-0" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3 font-serif">{u.name}</td>
                    <td className="px-5 py-3 text-neutral-400">{u.email}</td>
                    <td className="px-5 py-3 text-neutral-400">{u.country || "—"}</td>
                    <td className="px-5 py-3">{u.membership}</td>
                    <td className="px-5 py-3"><span className="overline text-[#d4af37]">{u.role}</span></td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => openEditUser(u)}
                          className="btn-outline-gold text-xs"
                          data-testid={`user-edit-${u.id}`}
                        >
                          <Pencil size={12} /> Modifier
                        </button>
                        <button
                          onClick={() => deleteUser(u)}
                          className="text-[#e6192b] hover:text-white transition-colors p-2"
                          title="Delete"
                          data-testid={`user-delete-${u.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {userEditModal && (
            <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setUserEditModal(null)} data-testid="user-edit-modal">
              <div className="bg-[#0a0a0a] border border-[#d4af37]/40 max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b border-[#161616] flex items-center justify-between">
                  <div>
                    <div className="overline">Modifier utilisateur</div>
                    <div className="font-serif text-xl mt-1">{userEditModal.email}</div>
                  </div>
                  <button onClick={() => setUserEditModal(null)} className="text-neutral-500 hover:text-white" data-testid="user-edit-close">
                    <X size={20} />
                  </button>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <UField label="Name" value={userForm.name} onChange={(v) => setUserForm({ ...userForm, name: v })} tid="uf-name" />
                  <UField label="Email" type="email" value={userForm.email} onChange={(v) => setUserForm({ ...userForm, email: v })} tid="uf-email" />
                  <UField label="Country" value={userForm.country} onChange={(v) => setUserForm({ ...userForm, country: v })} tid="uf-country" />
                  <UField label="Phone" value={userForm.phone} onChange={(v) => setUserForm({ ...userForm, phone: v })} tid="uf-phone" />
                  <div>
                    <div className="overline mb-2">Role</div>
                    <select
                      value={userForm.role}
                      onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                      className="w-full bg-[#0a0a0a] border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2.5 text-sm"
                      data-testid="uf-role"
                    >
                      <option value="student">student</option>
                      <option value="member">member</option>
                      <option value="trainer">trainer</option>
                      <option value="admin">admin</option>
                    </select>
                  </div>
                  <div>
                    <div className="overline mb-2">Membership</div>
                    <select
                      value={userForm.membership}
                      onChange={(e) => setUserForm({ ...userForm, membership: e.target.value })}
                      className="w-full bg-[#0a0a0a] border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2.5 text-sm"
                      data-testid="uf-membership"
                    >
                      <option value="non_member">non_member</option>
                      <option value="member">member</option>
                    </select>
                  </div>
                </div>
                <div className="p-6 border-t border-[#161616] flex items-center justify-between gap-3">
                  <button
                    onClick={() => deleteUser(userEditModal)}
                    className="text-[#e6192b] text-sm hover:text-white transition-colors inline-flex items-center gap-1.5"
                    data-testid="user-edit-delete"
                  >
                    <Trash2 size={14} /> Supprimer
                  </button>
                  <div className="flex gap-2">
                    <button onClick={() => setUserEditModal(null)} className="btn-outline-gold text-xs">Annuler</button>
                    <button onClick={saveUserModal} className="btn-imperial text-xs" data-testid="user-edit-save">
                      <Save size={12} /> Enregistrer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
      {section === "services" && (
        <section data-testid="admin-services">
          <h2 className="font-serif text-3xl mb-6">Service Orders</h2>
          {svcOrders.length === 0 ? (
            <div className="text-neutral-500 text-sm">No service orders yet.</div>
          ) : (
            <div className="card-imperial overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#0a0a0a] border-b border-[#1a1a1a]">
                  <tr>
                    <th className="text-left px-5 py-3 overline">Customer</th>
                    <th className="text-left px-5 py-3 overline">Package</th>
                    <th className="text-left px-5 py-3 overline">Amount</th>
                    <th className="text-left px-5 py-3 overline">Method</th>
                    <th className="text-left px-5 py-3 overline">Status</th>
                    <th className="text-left px-5 py-3 overline">Onboarding</th>
                    <th className="text-left px-5 py-3 overline">Project</th>
                    <th className="text-right px-5 py-3 overline">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {svcOrders.map((o) => (
                    <tr key={o.id} className="border-b border-[#141414] last:border-0" data-testid={`svc-row-${o.id}`}>
                      <td className="px-5 py-3">
                        <div className="font-serif">{o.user?.name || o.name}</div>
                        <div className="text-xs text-neutral-500">{o.user?.email || o.email}</div>
                      </td>
                      <td className="px-5 py-3 font-condensed uppercase tracking-widest text-xs text-[#d4af37]">{o.package}</td>
                      <td className="px-5 py-3">${o.amount}</td>
                      <td className="px-5 py-3">{o.payment_method}</td>
                      <td className="px-5 py-3">
                        <span className={
                          o.payment_status === "paid" ? "text-[#d4af37]"
                          : o.payment_status === "pending_review" ? "text-yellow-400"
                          : "text-neutral-500"
                        }>{o.payment_status}</span>
                      </td>
                      <td className="px-5 py-3">
                        {o.onboarding_status === "submitted" ? (
                          <span className="text-[#d4af37]">✓ submitted</span>
                        ) : (
                          <span className="text-neutral-500">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <select
                          value={o.project_status || "pending"}
                          onChange={(ev) => updateProjectStatus(o.id, ev.target.value)}
                          className="bg-[#0a0a0a] border border-[#222] focus:border-[#d4af37] outline-none px-2 py-1 text-xs"
                          data-testid={`svc-status-${o.id}`}
                        >
                          <option value="pending">pending</option>
                          <option value="in_design">in_design</option>
                          <option value="in_review">in_review</option>
                          <option value="delivered">delivered</option>
                        </select>
                      </td>
                      <td className="px-5 py-3 text-right space-x-2">
                        {o.screenshot_path && (
                          <button onClick={() => setPreviewUrl(downloadUrl(o.screenshot_path))} className="text-neutral-400 hover:text-[#d4af37] text-xs" data-testid={`svc-proof-${o.id}`}>
                            <Eye size={12} /> Proof
                          </button>
                        )}
                        {o.logo_path && (
                          <a href={downloadUrl(o.logo_path)} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-[#d4af37] text-xs inline-flex items-center gap-1" data-testid={`svc-logo-${o.id}`}>
                            <Download size={12} /> Logo
                          </a>
                        )}
                        {o.payment_status === "pending_review" && (
                          <>
                            <button onClick={() => approveService(o.id)} className="btn-imperial text-xs" data-testid={`svc-approve-${o.id}`}>
                              <Check size={12} /> Approve
                            </button>
                            <button onClick={() => rejectService(o.id)} className="text-[#e6192b] text-xs" data-testid={`svc-reject-${o.id}`}>
                              <X size={14} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {section === "whatsapp" && (
        <section data-testid="admin-whatsapp">
          <h2 className="font-serif text-3xl mb-6">WhatsApp follow-up accounts</h2>
          <p className="text-sm text-neutral-400 mb-6 max-w-2xl">
            Add team numbers students can reach for follow-up. All active accounts appear on the public WhatsApp widget.
          </p>

          <form onSubmit={saveWhatsapp} className="card-imperial p-5 mb-6 grid md:grid-cols-5 gap-3" data-testid="wa-form">
            <input
              required
              placeholder="Label (e.g. Jocelyn · USA)"
              value={waForm.label}
              onChange={(e) => setWaForm({ ...waForm, label: e.target.value })}
              className="bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
              data-testid="wa-label"
            />
            <input
              required
              placeholder="Phone (e.g. 15617187259)"
              value={waForm.phone}
              onChange={(e) => setWaForm({ ...waForm, phone: e.target.value })}
              className="bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
              data-testid="wa-phone"
            />
            <input
              placeholder="Region"
              value={waForm.region}
              onChange={(e) => setWaForm({ ...waForm, region: e.target.value })}
              className="bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
              data-testid="wa-region"
            />
            <input
              placeholder="Note"
              value={waForm.note}
              onChange={(e) => setWaForm({ ...waForm, note: e.target.value })}
              className="bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
              data-testid="wa-note"
            />
            <button type="submit" className="btn-imperial text-xs" data-testid="wa-submit">
              <Plus size={12} /> {waEditId ? "Update" : "Add"}
            </button>
          </form>

          {waAccounts.length === 0 ? (
            <div className="text-neutral-500 text-sm">No accounts yet.</div>
          ) : (
            <div className="card-imperial overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#0a0a0a] border-b border-[#1a1a1a]">
                  <tr>
                    <th className="text-left px-5 py-3 overline">Label</th>
                    <th className="text-left px-5 py-3 overline">Phone</th>
                    <th className="text-left px-5 py-3 overline">Region</th>
                    <th className="text-left px-5 py-3 overline">Note</th>
                    <th className="text-left px-5 py-3 overline">Status</th>
                    <th className="text-right px-5 py-3 overline">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {waAccounts.map((w) => (
                    <tr key={w.id} className="border-b border-[#141414] last:border-0" data-testid={`wa-row-${w.id}`}>
                      <td className="px-5 py-3 font-serif">{w.label}</td>
                      <td className="px-5 py-3 text-neutral-400">+{w.phone}</td>
                      <td className="px-5 py-3 text-neutral-400">{w.region || "—"}</td>
                      <td className="px-5 py-3 text-neutral-400 truncate max-w-[200px]">{w.note || "—"}</td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => toggleWhatsapp(w)}
                          className={`overline ${w.is_active ? "text-[#d4af37]" : "text-neutral-500"}`}
                          data-testid={`wa-toggle-${w.id}`}
                        >
                          {w.is_active ? "● ACTIVE" : "○ paused"}
                        </button>
                      </td>
                      <td className="px-5 py-3 text-right space-x-2">
                        <button
                          onClick={() => { setWaEditId(w.id); setWaForm({ label: w.label, phone: w.phone, region: w.region, note: w.note }); }}
                          className="text-neutral-400 hover:text-[#d4af37] text-xs"
                          data-testid={`wa-edit-${w.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteWhatsapp(w.id)}
                          className="text-neutral-400 hover:text-[#e6192b]"
                          data-testid={`wa-delete-${w.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {section === "competitors" && (
        <CompetitorsManagement />
      )}

      {section === "manual-reg" && isSuperAdmin && (
        <ManualRegistrationHub />
      )}
      {section === "manual-reg" && !isSuperAdmin && (
        <section className="card-imperial p-10 text-center" data-testid="manual-reg-locked">
          <div className="font-serif text-2xl text-[#e6192b]">Super-admin only</div>
          <p className="mt-3 text-neutral-400">This tool is restricted to the permanent super-admin account.</p>
        </section>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, accent, highlight }) {
  return (
    <div className={`card-imperial p-5 ${highlight ? "border-[#e6192b]/40" : ""}`} data-testid={`stat-${label}`}>
      <div className="overline">{label}</div>
      <div className={`font-serif text-4xl mt-2 ${accent ? "text-[#d4af37]" : ""}`}>{value}</div>
    </div>
  );
}

function UField({ label, type = "text", value, onChange, tid }) {
  return (
    <label className="block">
      <div className="overline mb-2">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2.5 text-sm"
        data-testid={tid}
      />
    </label>
  );
}

// =========================================================================
// Competition Management — All competitors table + edit modal (admin tab)
// =========================================================================
function CompetitorsManagement() {
  const { t } = useI18n();
  const [all, setAll] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", website_url: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const loadAll = async () => {
    try {
      const r = await api.get("/admin/competition");
      const items = (r.data || []).slice().sort((a, b) =>
        (b.created_at || "").localeCompare(a.created_at || "")
      );
      setAll(items);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadAll(); }, []);

  const openEdit = (c) => {
    setEditForm({
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
      website_url: c.website_url || "",
    });
    setEditing(c);
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/admin/competition/${editing.id}`, editForm);
      toast.success("Competitor updated");
      setEditing(null);
      loadAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (err.message || "Update failed"));
    } finally { setSavingEdit(false); }
  };

  const removeCompetitor = async (c) => {
    if (!window.confirm(`Permanently delete competitor ${c.email}?`)) return;
    try {
      await api.delete(`/admin/competition/${c.id}`);
      toast.success("Competitor removed");
      setEditing(null);
      loadAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (err.message || "Delete failed"));
    }
  };

  return (
    <section data-testid="admin-competitors" className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl flex items-center gap-3">
          <Trophy size={26} className="text-[#d4af37]" />
          {t("admin.competitionMgmt", "🏆 Competition Management")}
        </h2>
        <p className="text-neutral-400 text-sm mt-2 max-w-2xl">
          Strictly Competition 2026 participants — both online sign-ups and entries added through the Manual Registration tool. Click <span className="text-[#d4af37]">Modifier</span> to edit any field.
        </p>
      </div>

      <div data-testid="competitors-table">
        <div className="text-xs text-neutral-500 mb-3">
          {all.length} total · sorted by most recent registration
        </div>
        <div className="card-imperial overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-neutral-500 text-xs uppercase">
              <tr className="border-b border-[#1f1f1f]">
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Website</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Votes</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {all.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-neutral-500">No competitors yet.</td></tr>
              ) : all.map((c) => (
                <tr key={c.id} className="border-b border-[#161616] last:border-0 hover:bg-[#0c0c0c] transition-colors" data-testid={`competitor-row-${c.id}`}>
                  <td className="px-4 py-3">{c.name}</td>
                  <td className="px-4 py-3 text-neutral-400">{c.email}</td>
                  <td className="px-4 py-3 text-neutral-400">{c.phone || "—"}</td>
                  <td className="px-4 py-3 text-neutral-500 text-xs truncate max-w-[200px]">
                    {c.website_url
                      ? <a href={c.website_url} target="_blank" rel="noreferrer" className="hover:text-[#d4af37]">{c.website_url}</a>
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 text-[10px] font-condensed tracking-widest uppercase border border-[#d4af37]/40 text-[#d4af37]">
                      {c.source || (c.registered_manually_by ? "manual" : "online")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-condensed text-[#d4af37]">{c.votes || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openEdit(c)}
                      className="btn-outline-gold text-xs"
                      data-testid={`competitor-edit-${c.id}`}
                    >
                      <Pencil size={12} /> Modifier
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-5" data-testid="competitor-edit-modal" onClick={() => !savingEdit && setEditing(null)}>
          <div className="card-imperial w-full max-w-2xl p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="overline text-[#d4af37]">Edit competitor</div>
                <div className="font-serif text-2xl mt-1">{editing.name}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-neutral-400 hover:text-white" data-testid="competitor-edit-close"><X size={20} /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <UField label="Full name *" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} tid="competitor-edit-name" />
              <UField label="Email *" type="email" value={editForm.email} onChange={(v) => setEditForm({ ...editForm, email: v })} tid="competitor-edit-email" />
              <UField label="Phone" value={editForm.phone} onChange={(v) => setEditForm({ ...editForm, phone: v })} tid="competitor-edit-phone" />
              <UField label="Website URL" value={editForm.website_url} onChange={(v) => setEditForm({ ...editForm, website_url: v })} tid="competitor-edit-website" />
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={saveEdit} disabled={savingEdit || !editForm.name.trim() || !editForm.email.trim()} className="btn-imperial disabled:opacity-50 disabled:cursor-not-allowed" data-testid="competitor-edit-save">
                <Save size={14} /> {savingEdit ? "Saving…" : "Save changes"}
              </button>
              <button onClick={() => setEditing(null)} className="btn-outline-gold text-xs" data-testid="competitor-edit-cancel">Cancel</button>
              <button onClick={() => removeCompetitor(editing)} className="ml-auto text-xs text-[#e6192b] hover:text-white inline-flex items-center gap-1" data-testid="competitor-edit-delete">
                <Trash2 size={12} /> Delete competitor
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


// =========================================================================
// Manual Registration Hub — toggle: register a Course student vs Competitor
// =========================================================================
function ManualRegistrationHub() {
  const { t } = useI18n();
  const [mode, setMode] = useState("course"); // course | competition

  return (
    <section data-testid="admin-manual-reg" className="space-y-6">
      <div>
        <div className="overline mb-2">Super-admin tool</div>
        <h2 className="font-serif text-3xl">{t("admin.manualRegister", "Manual Registration")}</h2>
        <p className="text-neutral-400 text-sm mt-2 max-w-2xl">
          Register a lead on their behalf — choose whether they're joining a Course cohort or signing up for the Competition.
        </p>
      </div>

      <div className="card-imperial p-2 flex" data-testid="manual-mode-toggle">
        <button
          onClick={() => setMode("course")}
          data-testid="mode-toggle-course"
          className={`flex-1 px-4 py-3 text-xs font-condensed tracking-widest uppercase inline-flex items-center justify-center gap-2 transition-colors ${
            mode === "course" ? "bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/40" : "text-neutral-400 hover:text-white"
          }`}
        >
          <GraduationCap size={14} />
          {t("admin.manualModeCourse", "A Course")}
        </button>
        <button
          onClick={() => setMode("competition")}
          data-testid="mode-toggle-competition"
          className={`flex-1 px-4 py-3 text-xs font-condensed tracking-widest uppercase inline-flex items-center justify-center gap-2 transition-colors ${
            mode === "competition" ? "bg-[#e6192b]/15 text-[#e6192b] border border-[#e6192b]/40" : "text-neutral-400 hover:text-white"
          }`}
        >
          <Trophy size={14} />
          {t("admin.manualModeCompetition", "The Competition")}
        </button>
      </div>

      {mode === "course" ? <ManualCourseForm /> : <ManualCompetitionForm />}
    </section>
  );
}


function ManualCourseForm() {
  const { t } = useI18n();
  const initial = {
    name: "",
    email: "",
    phone: "",
    country: "",
    cohort: "monday_standard",
    mark_paid: false,
    source: "whatsapp",
  };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/admin/students/register", form);
      const cohortLabel = r.data?.cohort_label || "";
      const verb = r.data?.is_new_user ? "created" : "added enrollment for existing user";
      toast.success(`${t("admin.courseRegistered", "Student registered.")} (${cohortLabel} · ${verb})`);
      setForm(initial);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (err.message || "Registration failed"));
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="card-imperial p-6 md:p-8 space-y-5" data-testid="manual-course-form">
      <div className="grid md:grid-cols-2 gap-5">
        <UField label="Full name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} tid="course-reg-name" />
        <UField label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} tid="course-reg-email" />
        <UField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} tid="course-reg-phone" />
        <UField label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} tid="course-reg-country" />
        <label className="block md:col-span-2">
          <div className="overline mb-2 flex items-center gap-2">
            <BookOpen size={11} className="text-[#d4af37]" /> {t("admin.cohortSelect", "Cohort")} *
          </div>
          <select
            value={form.cohort}
            onChange={(e) => setForm({ ...form, cohort: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2.5 text-sm text-neutral-100"
            data-testid="course-reg-cohort"
          >
            <option value="monday_standard">{t("admin.cohortMondayStandard", "Monday cohort · Standard ($499)")}</option>
            <option value="monday_member">{t("admin.cohortMondayMember", "Monday cohort · Member ($399)")}</option>
            <option value="thursday_advanced">{t("admin.cohortThursdayAdvanced", "Thursday cohort · Advanced AI ($750)")}</option>
          </select>
        </label>
        <label className="block">
          <div className="overline mb-2">Lead source</div>
          <select
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2.5 text-sm text-neutral-100"
            data-testid="course-reg-source"
          >
            <option value="whatsapp">WhatsApp</option>
            <option value="phone">Phone</option>
            <option value="in_person">In-person</option>
            <option value="referral">Referral</option>
            <option value="email">Email</option>
            <option value="manual">Other</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-neutral-300 select-none mt-7">
          <input
            type="checkbox"
            checked={form.mark_paid}
            onChange={(e) => setForm({ ...form, mark_paid: e.target.checked })}
            data-testid="course-reg-mark-paid"
            className="accent-[#d4af37]"
          />
          {t("admin.markPaid", "Mark as already paid (skip approval queue)")}
        </label>
      </div>
      <div className="flex items-center gap-3">
        <button type="submit" disabled={submitting || !form.name.trim() || !form.email.trim()} className="btn-imperial disabled:opacity-50 disabled:cursor-not-allowed" data-testid="course-reg-submit">
          {submitting ? "Sending…" : t("admin.registerStudent", "Register & send welcome email")}
        </button>
        <button type="button" onClick={() => setForm(initial)} className="text-sm text-neutral-500 hover:text-white" data-testid="course-reg-reset">Reset</button>
      </div>
    </form>
  );
}


function ManualCompetitionForm() {
  const initial = { name: "", email: "", phone: "", website_url: "", source: "whatsapp" };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/admin/competition/register", form);
      toast.success(`Registered ${r.data?.name || form.email} — confirmation email queued.`);
      setForm(initial);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (err.message || "Registration failed"));
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="card-imperial p-6 md:p-8 space-y-5" data-testid="manual-competition-form">
      <div className="grid md:grid-cols-2 gap-5">
        <UField label="Full name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} tid="manual-reg-name" />
        <UField label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} tid="manual-reg-email" />
        <UField label="Phone number" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} tid="manual-reg-phone" />
        <UField label="Website URL (optional)" value={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })} tid="manual-reg-website" />
        <label className="block">
          <div className="overline mb-2">Lead source</div>
          <select
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2.5 text-sm text-neutral-100"
            data-testid="manual-reg-source"
          >
            <option value="whatsapp">WhatsApp</option>
            <option value="phone">Phone</option>
            <option value="in_person">In-person</option>
            <option value="referral">Referral</option>
            <option value="email">Email</option>
            <option value="manual">Other</option>
          </select>
        </label>
      </div>
      <div className="flex items-center gap-3">
        <button type="submit" disabled={submitting || !form.name.trim() || !form.email.trim()} className="btn-imperial disabled:opacity-50 disabled:cursor-not-allowed" data-testid="manual-reg-submit">
          {submitting ? "Sending…" : "Register & send confirmation email"}
        </button>
        <button type="button" onClick={() => setForm(initial)} className="text-sm text-neutral-500 hover:text-white" data-testid="manual-reg-reset">Reset</button>
      </div>
      <div className="text-[11px] text-neutral-500 flex items-center gap-1.5">
        <Trophy size={11} className="text-[#d4af37]" />
        One entry per email address — duplicates are rejected with a clear message. Edits live under {' '}
        <a href="#competitors" className="text-[#d4af37] hover:underline">🏆 Competition Management</a>.
      </div>
    </form>
  );
}
